import _defineProperty from "/Users/matt/dev/github.com/industriousapps/excelkits-client/node_modules/.pnpm/next@13.5.7_@babel+core@7.25.2_react-dom@18.3.1_react@18.3.1__react@18.3.1_sass@1.79.3/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import { getContextEditingSectionAttr } from '@/components/Editor/hooks/useEditingAreas';
import { chartColorPallet as DEFAULT_COLORS } from '@/config/colorPallets/universalDesignSystem';
import { deleteUndefinedValues } from '@/utils/objects';
import { buildChartRenderingHash, extractDataLabelProperty, getChartTitleAnnotationSettings, getSectionCanvasId } from '../utils';
import useLineEditingArea from '../useLineEditingArea';
import useSelections from '../../../misc/useSelections';
import { useLineStyles } from './stylesTheme';
import settings, { selectionConfig, labelsSelectionConfig, legendsSelectionConfig } from './settings';
import StyleVariables, { getSectionAttr, getSectionAttrSelector } from '../../../misc/StyleVariables';
import Header from '../../misc/Header';
import Loading from './Loading';
import { TmpLineChartJs } from './TmpChart';
import styles from './styles.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

const LineChart = _ref => {
  let {
    widgetId,
    sectionId,
    elementId,
    editing = false,
    isLoading = false,
    hasNoData = false,
    hasLegend: defaultHasLegend = false,
    config,
    chartData,
    xAxisTicksCallback,
    yAxisTicksCallback,
    openLegendsContextEditor
  } = _ref;
  const {
    lineThemeStyles
  } = useLineStyles(config);
  const {
    tooltip,
    chartStyle = 'line',
    animation = {}
  } = config;
  const {
    0: isLoadingDeps,
    1: setIsLoadingDeps
  } = useState(false);
  const {
    0: hasLoadedDeps,
    1: setHasLoadedDeps
  } = useState(false);
  const {
    handleChartClick,
    handleAxisClick
  } = useLineEditingArea({
    widgetId,
    sectionId,
    chartData
  }); // Load Chart Deps

  useEffect(() => {
    if (isLoadingDeps || hasLoadedDeps) {
      return;
    }

    setIsLoadingDeps(true);

    (async () => {
      const {
        default: Chart,
        LinearScale,
        CategoryScale,
        PointElement,
        LineElement,
        Title,
        Tooltip,
        Legend
      } = await import('chart.js/auto');
      const {
        default: ChartDataLabels
      } = await import('chartjs-plugin-datalabels');
      const {
        default: ChartAnnotation
      } = await import('chartjs-plugin-annotation');
      const {
        default: CustomBackground
      } = await import('../../plugins/customBackground');
      const {
        default: ActiveLineHighlight
      } = await import('../../plugins/activeLineHighlightPlugin');
      const {
        default: ResponsiveResizePlugin
      } = await import('../../plugins/responsiveResizePlugin');
      const {
        default: LegendPaddingPlugin
      } = await import('../../plugins/legendPadding');
      const {
        default: AxisLabelClickPlugin
      } = await import('../../plugins/axisLabelClickPlugin');
      Chart.register(LinearScale, CategoryScale, PointElement, LineElement, Title, Tooltip, Legend, ChartDataLabels, ChartAnnotation, CustomBackground, ActiveLineHighlight, ResponsiveResizePlugin, LegendPaddingPlugin, AxisLabelClickPlugin); // Disable datalabels by default

      if (Chart.defaults.plugins.datalabels) {
        Chart.defaults.plugins.datalabels.display = false;
      }

      setHasLoadedDeps(true);
      setIsLoadingDeps(false);
    })(); // eslint-disable-next-line react-hooks/exhaustive-deps

  }, []); // Data & Loading States

  const hasDrawableData = Boolean(chartData.datasets.length);
  const isShowingLoader = !hasLoadedDeps || !hasDrawableData;
  const chartRenderingHash = buildChartRenderingHash({
    animation,
    themeStyles: lineThemeStyles
  }); // Create chart options

  const options = useMemo(() => {
    var _config$styles, _config$globalSeriesO, _config$globalSeriesO2;

    const animation = config.animation || {};
    const chartTitle = config.chartTitle ? {
      display: true,
      text: config.chartTitle,
      font: {
        family: lineThemeStyles.titleFontFamily,
        size: lineThemeStyles.titleFontSize,
        lineHeight: lineThemeStyles.titleLineHeight
      },
      color: lineThemeStyles.titleColor
    } : {};
    const legendPosition = config.legendPosition || 'bottom';
    const hasLegend = config.hasLegend ?? defaultHasLegend;
    const isAnimationDisabled = animation.enabled !== undefined && !animation.enabled;
    const axisFont = {
      font: {
        family: lineThemeStyles.chartAxisFontFamily,
        size: lineThemeStyles.chartAxisFontSize,
        weight: lineThemeStyles.chartAxisFontWeight
      },
      color: lineThemeStyles.chartAxisFontColor
    };
    const isYAxisTitlePositionOver = config.yAxisPosition === 'over' && Boolean(config.yAxisTitle);
    return {
      indexAxis: 'x',
      responsive: true,
      maintainAspectRatio: false,
      pointRadius: lineThemeStyles.pointRadius,
      pointStyle: lineThemeStyles.pointStyle ?? 'circle',
      pointBorderWidth: 0,
      animation: _objectSpread(_objectSpread({}, deleteUndefinedValues(animation)), {}, {
        duration: isAnimationDisabled ? 0 : animation.duration
      }),
      elements: {
        line: {
          tension: lineThemeStyles.tension // Bezier curve tension of the line

        }
      },
      plugins: {
        title: chartTitle,
        annotation: isYAxisTitlePositionOver ? getChartTitleAnnotationSettings({
          title: config.yAxisTitle,
          axis: 'y',
          widgetId,
          sectionId,
          axisFont
        }) : undefined,
        datalabels: {
          display: true,
          borderRadius: lineThemeStyles.chartDataLabelBorderRadius,
          color: context => extractDataLabelProperty(context, 'color', lineThemeStyles.chartDataLabelColor),
          align: context => extractDataLabelProperty(context, 'align', 'center'),
          anchor: context => extractDataLabelProperty(context, 'anchor', 'end'),
          rotation: context => extractDataLabelProperty(context, 'rotation', 0),
          clamp: context => extractDataLabelProperty(context, 'clamp', true),
          formatter: function (value, context) {
            var _context$dataset$data;

            // Hide labels if not enabled or with value 0
            const dataLabel = (_context$dataset$data = context.dataset.datalabels) === null || _context$dataset$data === void 0 ? void 0 : _context$dataset$data[context.dataIndex];
            if (!(dataLabel !== null && dataLabel !== void 0 && dataLabel.display) || value === 0) return '';
            return dataLabel.template ?? value;
          },
          backgroundColor: function (context) {
            var _context$dataset$data2;

            // Hide labels if not enabled or with value 0
            const dataLabel = (_context$dataset$data2 = context.dataset.datalabels) === null || _context$dataset$data2 === void 0 ? void 0 : _context$dataset$data2[context.dataIndex];
            if (!(dataLabel !== null && dataLabel !== void 0 && dataLabel.display)) return '';
            return lineThemeStyles.chartDataLabelBackgroundColor;
          },
          font: axisFont.font
        },
        legend: deleteUndefinedValues(_objectSpread({
          align: ['top', 'bottom'].includes(legendPosition) ? 'start' : undefined,
          position: legendPosition,
          display: hasLegend,
          labels: {
            pointStyle: 'rectRounded',
            usePointStyle: true,
            font: {
              family: lineThemeStyles.legendFontFamily,
              size: lineThemeStyles.legendFontSize,
              lineHeight: lineThemeStyles.legendLineHeight
            },
            color: lineThemeStyles.legendColor,
            // Remove unwanted datasets from legend
            filter: item => item.text !== undefined
          }
        }, editing && openLegendsContextEditor && {
          onClick: e => openLegendsContextEditor(e, widgetId, sectionId)
        })),
        legendPadding: {
          padding: isYAxisTitlePositionOver ? 44 : 24 // Custom padding between legend and chart

        },
        tooltip: {
          enabled: true,
          usePointStyle: true,
          cornerRadius: 0,
          callbacks: {
            title: _ref2 => {
              let [item] = _ref2;
              // @ts-ignore
              return (item === null || item === void 0 ? void 0 : item.dataset.titles[item.dataIndex]) || item.label;
            },
            label: item => {
              return ` ${// @ts-ignore
              (item === null || item === void 0 ? void 0 : item.dataset.templates[item.dataIndex]) || item.parsed.y}`;
            }
          }
        },
        activeLineHighlightPlugin: {
          enable: editing,
          dimOpacity: 0.15,
          // Opacity for non-hovered lines.
          hoverCursor: 'pointer',
          hoverDistanceThreshold: 10,
          // Pixels: how close the mouse must be to a line.
          extraElements: [`[${getContextEditingSectionAttr()}]`, '[data-floating-ui-portal]']
        },
        customCanvasBackgroundColor: {
          color: (config === null || config === void 0 ? void 0 : (_config$styles = config.styles) === null || _config$styles === void 0 ? void 0 : _config$styles.backgroundColor) || lineThemeStyles.chartBackgroundColor
        },
        axisLabelClickPlugin: {
          enabled: true,
          onXAxisLabelClick: handleAxisClick,
          onXAxisTitleClick: handleAxisClick,
          onYAxisLabelClick: handleAxisClick,
          onYAxisTitleClick: handleAxisClick
        }
      },
      layout: {
        padding: {
          left: config.yAxisTitle && !isYAxisTitlePositionOver ? -6 : -16,
          right: 0,
          top: hasLegend && legendPosition === 'top' ? 0 : isYAxisTitlePositionOver ? 56 : 0,
          bottom: hasLegend || config.xAxisTitle ? 0 : -lineThemeStyles.xAxisTicksPadding
        }
      },
      scales: {
        x: deleteUndefinedValues({
          min: (_config$globalSeriesO = config.globalSeriesOptions) !== null && _config$globalSeriesO !== void 0 && _config$globalSeriesO.forceZeroBaseline ? 0 : undefined,
          grid: {
            color: lineThemeStyles.xAxisGridVisible ? lineThemeStyles.xAxisGridColor : 'transparent',
            drawTicks: false,
            tickLength: 20,
            lineWidth: lineThemeStyles.xAxisGridBorderWidth ?? 1
          },
          border: {
            dash: lineThemeStyles.xAxisGridBorderDash ?? [5, 5],
            display: false
          },
          ticks: deleteUndefinedValues(_objectSpread(_objectSpread({}, axisFont), {}, {
            display: config.xAxisDisplay ?? true,
            padding: lineThemeStyles.xAxisTicksPadding,
            crossAlign: 'near',
            // Custom value ticks callback for vertical line charts
            callback: xAxisTicksCallback ? xAxisTicksCallback : undefined
          })),
          title: config.xAxisTitle ? _objectSpread({
            display: config.xAxisDisplay ?? true,
            text: config.xAxisTitle,
            align: config.xAxisPosition
          }, axisFont) : undefined,
          afterFit: scale => {
            scale.paddingLeft -= 3;
            scale.paddingRight -= 16;
          }
        }),
        y: deleteUndefinedValues({
          min: (_config$globalSeriesO2 = config.globalSeriesOptions) !== null && _config$globalSeriesO2 !== void 0 && _config$globalSeriesO2.forceZeroBaseline ? 0 : config.yScaleMin,
          max: config.yScaleMax,
          stacked: config.chartStyle === 'stackedArea',
          grid: {
            color: lineThemeStyles.yAxisGridVisible ? lineThemeStyles.yAxisGridColor : 'transparent',
            drawTicks: false,
            lineWidth: lineThemeStyles.yAxisGridBorderWidth ?? 1
          },
          border: {
            dash: lineThemeStyles.yAxisGridBorderDash ?? [5, 5],
            display: false
          },
          ticks: deleteUndefinedValues(_objectSpread(_objectSpread({}, axisFont), {}, {
            display: config.yAxisDisplay ?? true,
            padding: lineThemeStyles.yAxisTicksPadding,
            // Custom value ticks callback for horizontal line charts
            callback: yAxisTicksCallback ? yAxisTicksCallback : undefined
          })),
          title: config.yAxisTitle && !isYAxisTitlePositionOver ? _objectSpread({
            display: config.yAxisDisplay ?? true,
            text: config.yAxisTitle,
            align: config.yAxisPosition
          }, axisFont) : undefined
        })
      },
      onClick: editing ? (event, activeElements, chart) => handleChartClick(event, activeElements, chart, ['area', 'stackedArea'].includes(chartStyle)) : undefined
    };
  }, [widgetId, sectionId, config, chartStyle, editing, defaultHasLegend, lineThemeStyles, xAxisTicksCallback, yAxisTicksCallback, openLegendsContextEditor, handleChartClick, handleAxisClick]);
  return /*#__PURE__*/_jsxs("div", _objectSpread(_objectSpread(_objectSpread({}, elementId && {
    id: elementId
  }), {}, {
    className: styles.container
  }, getSectionAttr(widgetId, sectionId)), {}, {
    children: [/*#__PURE__*/_jsx(Header, {
      widgetId: widgetId,
      sectionId: sectionId,
      editing: editing,
      config: config,
      tooltip: tooltip,
      className: styles.header
    }), isLoading || hasNoData ? /*#__PURE__*/_jsx(Loading, {
      editing: editing
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [isShowingLoader ? /*#__PURE__*/_jsx(Loading, {
        editing: editing
      }) : /*#__PURE__*/_jsx(_Fragment, {}), /*#__PURE__*/_jsx("div", {
        className: clsx(styles.main, isShowingLoader ? styles['main--hide'] : ''),
        children: hasLoadedDeps ? /*#__PURE__*/_jsx(TmpLineChartJs, {
          id: getSectionCanvasId(widgetId, sectionId) // @ts-expect-error dataset types mismatch
          ,
          data: chartData,
          options: options
        }, chartRenderingHash) : /*#__PURE__*/_jsx(_Fragment, {})
      })]
    }), /*#__PURE__*/_jsx(StyleVariables, {
      selector: getSectionAttrSelector(widgetId, sectionId),
      data: _objectSpread(_objectSpread({}, (settings === null || settings === void 0 ? void 0 : settings.styles) || {}), (config === null || config === void 0 ? void 0 : config.styles) || {})
    })]
  }));
};

export default LineChart;
export { DEFAULT_COLORS, useSelections, selectionConfig, labelsSelectionConfig, legendsSelectionConfig };